import * as d3 from "d3";
import { isValueValid } from "@/components/VivoPlots/functions/utils/valueUtils";
import { palettes } from "@/components/VivoPlots/functions/drawPlotData/utils/palletes";
import { hasSelectedIdConfig } from "@/components/VivoPlots/functions/utils/selectedIdUtils";
import {
  normalizeString as normalizeId,
  normalizeFillValue,
} from "@/utils/stringUtils";
import type { PlotTableView, scaleType } from "@/components/VivoPlots/types";
import { getBarplotSortAxes } from "@/components/VivoPlots/utils/barplotSorting";
import {
  buildVisitLabelMap,
  isVisitAxis,
  sortVisitDomain,
  visitKey,
} from "@/components/VivoPlots/utils/visitCategories";
import {
  EMPTY_TABLE_VIEW,
  buildBarTableView,
  buildScatterTableView,
  buildSwarmTableView,
} from "./plotTableView";

export type PlotDataResult = {
  scales: (scaleType & {
    domain?: unknown[];
    range?: unknown[];
    strokeRange?: unknown[];
    label?: string;
  })[];
  data?: Record<string, unknown>[];
  nestedData?: unknown[];
  plotClassName: string;
  noData: boolean;
  notPlottable?: boolean;
  maxLabelLength?: number;
  maxCategoryLabelLength?: number;
  pairingStats?: { dropped: number; hasRepeats: boolean };
  tableView: PlotTableView;
};

export function createPlotData(
  data: unknown,
  type: unknown,
  scales: unknown,
  config: unknown,
): PlotDataResult;

export function createPlotData(data, type, scales, config) {
  if (data?.length === 0 || !data)
    return {
      data: [],
      scales: [],
      plotClassName: "",
      noData: true,
      notPlottable: true,
      tableView: EMPTY_TABLE_VIEW,
    };

  if (type === "scatter") {
    const pairFields = getPairFields(config);
    const plotData = createScatterPlotData(data, scales, config);
    return {
      ...plotData,
      tableView: buildScatterTableView(plotData.data, scales, pairFields),
    };
  }

  if (type === "swarm-box") {
    const plotData = createBoxPlotData(data, scales, config);
    return {
      ...plotData,
      tableView: buildSwarmTableView(plotData.data, plotData.scales),
    };
  }

  if (type === "barplot") {
    const plotData = createBarplotData(data, scales, config);
    return {
      ...plotData,
      tableView: buildBarTableView(plotData, plotData.scales),
    };
  }
}

const createCategoricalDomain = (data, categoricalScale) => {
  if (!categoricalScale) return [];
  let categoricalDomain = categoricalScale?.predefinedDomain
    ? categoricalScale.predefinedDomain
    : Array.isArray(data[0]?.[categoricalScale.dataMapping])
      ? [
          ...new Set(
            data
              .flatMap((d) => d[categoricalScale.dataMapping])
              .map(normalizeFillValue),
          ),
        ]
      : [
          ...new Set(
            data.map((d) =>
              normalizeFillValue(d[categoricalScale.dataMapping]),
            ),
          ),
        ];

  if (categoricalScale.numericScale) {
    const maxValue = Math.max(...categoricalDomain.map(Number));
    categoricalDomain = Array.from({ length: maxValue + 1 }, (_, i) => i);
  }

  if (categoricalDomain.length === 0) {
    categoricalDomain = undefined;
  }
  return categoricalDomain;
};

const getScatterRawValue = (value) => {
  if (typeof value === "string") {
    const trimmedValue = value.trim();
    return trimmedValue === "" ? undefined : trimmedValue;
  }

  return value;
};

const getScatterNumericValue = (value) => {
  const normalizedValue = getScatterRawValue(value);
  if (normalizedValue === null || normalizedValue === undefined) {
    return undefined;
  }

  const numericValue = +normalizedValue;
  return isNaN(numericValue) ? undefined : numericValue;
};

const getScatterDateValue = (value) => {
  const normalizedValue = getScatterRawValue(value);
  if (!isValueValid(normalizedValue)) {
    return undefined;
  }

  const timestamp = new Date(normalizedValue).getTime();
  return isNaN(timestamp) ? undefined : timestamp;
};

const getScatterAxisValue = (value, isDate) => {
  return isDate ? getScatterDateValue(value) : getScatterNumericValue(value);
};

const getScatterDomainFromPoints = (plotData, axis, isDate) => {
  const values = plotData.map((point) => point[axis]).filter(isValueValid);

  if (values.length === 0) {
    return [0, 0];
  }

  if (isDate) {
    const max = d3.max(values);
    const min = d3.min(values);
    return [max ?? 0, min ?? 0];
  }

  const max = d3.max(values);
  const min = d3.min(values);

  return [max != null ? max * 1.001 : 0, min != null ? min * 0.999 : 0];
};

const normalizeMissingSubgroupValue = (value) => {
  if (value === null || value === undefined) {
    return "none";
  }

  if (typeof value === "string") {
    return value.trim() === "" ? "none" : value;
  }

  return value;
};

const getPairFields = (config) => {
  const pairFields = config?.pairFields ?? config?.pairKeys ?? [];

  if (!Array.isArray(pairFields)) return [];

  return [...new Set(pairFields)].filter(
    (field) => typeof field === "string" && field.trim() !== "",
  );
};

const encodeRawTupleValue = (value) => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";

  const type = typeof value;
  if (type === "string") return `string:${value.length}:${value}`;
  if (type === "number") return `number:${Number.isNaN(value) ? "NaN" : value}`;
  if (type === "boolean") return `boolean:${value ? 1 : 0}`;
  if (type === "bigint") return `bigint:${value.toString()}`;
  if (type === "symbol") return `symbol:${value.toString()}`;
  if (type === "function") return `function:${value.name || "anonymous"}`;

  try {
    return `object:${JSON.stringify(value)}`;
  } catch {
    return `object:${Object.prototype.toString.call(value)}`;
  }
};

// The row's value in the screen's selection-identifier column, exactly as the
// data carries it. `subjectId` feeds highlight matching, which normalizes it;
// `entityKey` feeds navigation, which cannot. Both must read the same value, so
// they read it here.
const getSelectionValue = (
  config: { selectedIdName?: string },
  // The rows reaching this file are untyped upstream; annotating them properly
  // is a separate job from making these two fields read the same value.
   
  row: any,
) => (config.selectedIdName && row[config.selectedIdName]) || undefined;

const getPairKey = (row, pairFields) =>
  pairFields
    .map((field, index) => `${index}:${encodeRawTupleValue(row?.[field])}`)
    .join("|");

const buildPairingStats = (plotData, dataLength, pairFields) => {
  if (!pairFields.length) return undefined;

  const seenKeys = new Set();
  let hasRepeats = false;

  for (const point of plotData) {
    const key = getPairKey(point, pairFields);
    if (seenKeys.has(key)) {
      hasRepeats = true;
    } else {
      seenKeys.add(key);
    }
  }

  return {
    dropped: Math.max(dataLength - plotData.length, 0),
    hasRepeats,
  };
};

const createScatterPlotData = (data, scales, config) => {
  const continuousScaleBottom = scales.find(
    (scale) => scale.position === "bottom",
  );
  const continuousScaleLeft = scales.find((scale) => scale.position === "left");
  const fillScale = scales.find((scale) => scale.type === "fill") || {};
  const pairFields = getPairFields(config);

  const xIsDate = fieldHasDates(data, continuousScaleBottom);
  const yIsDate = fieldHasDates(data, continuousScaleLeft);

  const plotData = data
    .map((d) => {
      return {
        ...d,
        fill: normalizeFillValue(d[fillScale.dataMapping]),
        x: getScatterAxisValue(d[continuousScaleBottom.dataMapping], xIsDate),
        y: getScatterAxisValue(d[continuousScaleLeft.dataMapping], yIsDate),
        xLabel: d[continuousScaleBottom.dataMapping],
        yLabel: d[continuousScaleLeft.dataMapping],
        subjectId: getSelectionValue(config, d),
        // Set only where one point is one source row; a point built from
        // several rows leaves it unset and drawCircles will not link it.
        entityKey: getSelectionValue(config, d),

        ssjIdValue: (config.ssjIdName && d[config.ssjIdName]) || undefined,
      };
    })
    .filter((d) => isValueValid(d.x) && isValueValid(d.y));

  const pairingStats = buildPairingStats(plotData, data.length, pairFields);

  let continuousDomainBottom = continuousScaleBottom?.predefinedDomain
    ? continuousScaleBottom.predefinedDomain
    : getScatterDomainFromPoints(plotData, "x", xIsDate);
  let continuousDomainLeft = continuousScaleLeft?.predefinedDomain
    ? continuousScaleLeft.predefinedDomain
    : getScatterDomainFromPoints(plotData, "y", yIsDate);
  const fillDomain = fillScale?.predefinedDomain
    ? fillScale.predefinedDomain
    : [...new Set(plotData.map((d) => d.fill))];
  if (continuousDomainBottom.length < 2) {
    continuousDomainBottom = [
      continuousDomainBottom[0],
      continuousDomainBottom[0],
    ];
  }
  if (continuousDomainLeft.length < 2) {
    continuousDomainLeft = [continuousDomainLeft[0], continuousDomainLeft[0]];
  }

  const scalesWithDomains = scales
    .map((scale) => {
      if (scale.position === "bottom") {
        return {
          ...scale,
          domain: continuousDomainBottom,
          type: xIsDate ? "time" : "continuous",
        };
      }
      if (scale.position === "left") {
        return {
          ...scale,
          domain: continuousDomainLeft,
          type: yIsDate ? "time" : "continuous",
        };
      }
      if (scale.type === "fill") {
        const paletteConfig = palettes[config?.palette || "Blue"] || {};

        return {
          ...scale,
          domain: fillDomain,
          range: [...(scale?.range || []), ...(paletteConfig.fill || [])],
          strokeRange: [
            ...(scale?.strokeRange || []),
            ...(paletteConfig.stroke || []),
          ],
        };
      }
      return scale;
    })
    .filter((scale) => scale.type !== "subgroup");

  return {
    scales: scalesWithDomains,
    data: plotData,
    plotClassName: config.plotClassName,
    noData: data.length === 0 || plotData.length === 0,
    notPlottable: plotData.length === 0,
    ...(pairingStats ? { pairingStats } : {}),
  };
};

/**
 * Longest category name on the axis, used to size the room a rotated category
 * axis needs. Unlike `maxLabelLength` this is deliberately uncapped: the cap
 * belongs to the layout that spends the space, not to the measurement.
 */
const longestCategoryLabel = (domain: unknown[]): number =>
  (domain ?? []).reduce<number>(
    (longest, label) => Math.max(longest, String(label ?? "").length),
    0,
  );

const createBoxPlotData = (data, scales, config) => {
  const categoricalScale = scales.find((scale) => scale.type === "categorical");

  if (
    data.length === 0 ||
    categoricalScale?.dataMapping === "none" ||
    !categoricalScale?.dataMapping
  ) {
    return {
      scales: scales,
      data: [],
      plotClassName: config.plotClassName,
      noData: true,
      notPlottable: false,
    };
  }
  const continuousScale = scales.find((scale) => scale.type === "continuous");

  const subgroupScale = scales.find(
    (scale) =>
      scale.type === "subgroup" &&
      scale.dataMapping !== "none" &&
      categoricalScale.dataMapping !== scale.dataMapping,
  );
  const fillScale = scales.find((scale) => scale.type === "fill") || {};

  let categoricalDomain = createCategoricalDomain(data, categoricalScale);

  if (config.xSortDirection !== "none") {
    categoricalDomain = sortDomain(
      categoricalDomain,
      config.xSortDirection,
      categoricalScale.position,
      data,
      categoricalScale.dataMapping,
    );
  }

  const subgroupDomain = [
    ...new Set(
      (createCategoricalDomain(data, subgroupScale) || []).map(
        normalizeMissingSubgroupValue,
      ),
    ),
  ];

  let continuousDomain = continuousScale.predefinedDomain
    ? continuousScale.predefinedDomain
    : getContinuousDomain(data, continuousScale);

  if (continuousDomain.length < 2) {
    continuousDomain = [continuousDomain[0], continuousDomain[0]];
  }

  const fillDomain = fillScale?.predefinedDomain
    ? fillScale?.predefinedDomain
    : [
        ...new Set(
          data.map((d) => normalizeFillValue(d[fillScale?.dataMapping])),
        ),
      ];

  let scalesWithDomains = scales.map((scale) => {
    if (scale.type === "categorical") {
      return { ...scale, domain: categoricalDomain };
    }
    if (scale.type === "subgroup" && scale.dataMapping !== "none") {
      return { ...subgroupScale, domain: subgroupDomain };
    }
    if (scale.type === "continuous") {
      return { ...scale, domain: continuousDomain };
    }
    if (scale.type === "fill") {
      const predefinedRange = Object.values(scale?.predefinedRange || {});
      const paletteConfig = palettes[config?.palette || "Blue"] || {};

      return {
        ...scale,
        domain: fillDomain,
        range: [...predefinedRange, ...(paletteConfig.fill || [])],
        strokeRange: [...predefinedRange, ...(paletteConfig.stroke || [])],
      };
    }

    return scale;
  });

  if (!subgroupDomain.length) {
    scalesWithDomains = scalesWithDomains.filter(
      (scale) => scale.type !== "subgroup",
    );
  }

  const nestedData = d3
    .groups(
      data,
      (d) => normalizeFillValue(d[categoricalScale.dataMapping]),
      (d) => normalizeMissingSubgroupValue(d[subgroupScale?.dataMapping]),
    )
    .filter(([key]) => categoricalDomain.includes(key))
    .map(([key, subgroups]) => {
      return subgroups.map(([subkey, values]) => {
        const normalizedSubkey = normalizeMissingSubgroupValue(subkey);

        const individualValues = values.reduce(
          (
            acc: {
              subjectId: string;
              entityKey: string;
              ssjIdValue: string;
              value: number;
              isOutlier: boolean;
              fill: string;
            }[],
            val,
          ) => {
            if (isValueValid(val[continuousScale.dataMapping])) {
              let value = 0;
              if (isNaN(+val[continuousScale.dataMapping])) {
                value = 0;
              } else {
                value = +val[continuousScale.dataMapping];
              }

              const upperLimit = continuousScale.upperLimitVariable
                ? val[continuousScale.upperLimitVariable]
                : config.upperLimit;
              const lowerLimit = continuousScale.lowerLimitVariable
                ? val[continuousScale.lowerLimitVariable]
                : config.lowerLimit;

              acc.push({
                fill: fillScale.predefinedDomain
                  ? fillScale.predefinedDomain.find(
                      (d) =>
                        d === normalizeFillValue(val[fillScale.dataMapping]),
                    )
                  : normalizeFillValue(val[fillScale.dataMapping]),
                subjectId: getSelectionValue(config, val),
                entityKey: getSelectionValue(config, val),

                ssjIdValue:
                  (config.ssjIdName && val[config.ssjIdName]) || undefined,
                value,
                isOutlier: continuousScale.outOfRangeFunction
                  ? continuousScale.outOfRangeFunction(val)
                  : (upperLimit != null && value > upperLimit) ||
                    (lowerLimit != null && value < lowerLimit),
              });
            }
            return acc;
          },
          [],
        ) as {
          subjectId: string;
          entityKey: string;
          ssjIdValue: string;
          value: number;
          isOutlier: boolean;
          fill: string;
        }[];

        if (individualValues.length === 0) return;
        const subgroupValues = individualValues.map((d) => d.value);
        const q1 = d3.quantile(subgroupValues, 0.25);
        const median = d3.quantile(subgroupValues, 0.5);
        const q3 = d3.quantile(subgroupValues, 0.75);
        const lowerWhisker = d3.quantile(subgroupValues, 0.025);
        const upperWhisker = d3.quantile(subgroupValues, 0.975);
        const mean = d3.mean(subgroupValues);
        const std = d3.deviation(subgroupValues);
        const total = subgroupValues.length;

        const subgroupStats = {
          q1,
          median,
          q3,
          lowerWhisker,
          upperWhisker,
          mean,
          std,
          total,
        };

        return {
          categoryMapping: categoricalScale.dataMapping,
          category: key,
          subgroup: normalizedSubkey,

          stats: subgroupStats,

          values: individualValues,
        };
      });
    })
    .flat()
    .filter((d) => d);

  const plotData = data
    .map((datum) =>
      subgroupScale
        ? {
            ...datum,
            [subgroupScale.dataMapping]: normalizeMissingSubgroupValue(
              datum[subgroupScale.dataMapping],
            ),
          }
        : datum,
    )
    .filter((datum) => {
      const categoricalValue = normalizeFillValue(
        datum[categoricalScale.dataMapping],
      );
      const hasValidCategory = categoricalDomain.includes(categoricalValue);
      const hasValidValue = isValueValid(datum[continuousScale.dataMapping]);

      return hasValidCategory && hasValidValue;
    });

  const maxContinuousLabel = [
    ...new Set(data.map((d) => d[continuousScale.dataMapping])),
  ].reduce((acc: string, d) => {
    let label = d?.toString();

    if (
      continuousScale.dictionary &&
      Object.keys(continuousScale.dictionary).length > 0
    ) {
      if (continuousScale.dictionary[d?.toString()]) {
        label = continuousScale.dictionary[d?.toString()];
      }
    }
    if (label?.length > acc.length) {
      return label;
    }
    return acc;
  }, "") as string;

  const maxLabelLength = Math.max(maxContinuousLabel.length * 1.2, 4);
  const noData = nestedData.length === 0 || plotData.length === 0;

  return {
    scales: scalesWithDomains,
    data: plotData,
    nestedData,
    plotClassName: config.plotClassName || continuousScale.dataMapping,
    noData,
    maxLabelLength,
    maxCategoryLabelLength: longestCategoryLabel(categoricalDomain),
  };
};

const createBarplotData = (data, scales, config) => {
  const { maxBarCount } = config;
  const hasSelectedId = hasSelectedIdConfig(config);

  const subjectData = hasSelectedId
    ? data.filter(
        (d) =>
          normalizeId(d[config.selectedIdName]) ===
          normalizeId(config.selectedIdValue),
      )
    : [];

  const categoricalScale = scales.find((scale) => scale.type === "categorical");
  const subgroupScale = scales.find(
    (scale) =>
      scale.type === "subgroup" &&
      scale.dataMapping !== "none" &&
      scale.dataMapping &&
      categoricalScale.dataMapping !== scale.dataMapping,
  );

  const stackScale = scales.find(
    (scale) => scale.type === "fill" && scale.dataMapping !== "none",
  );

  const totalScale = scales.find((scale) => scale.type === "total");

  const scalesWithDataMapping = scales
    .map((scale) => {
      return {
        ...scale,
        dataMapping: scale.dataMapping ? scale.dataMapping : undefined,
      };
    })
    .filter((scale) => scale.dataMapping && scale.type !== "total");

  // A study design can carry the same visit twice under different casing (two
  // source systems feeding one trial), which used to draw two identical-looking
  // bars. Collapse the variants onto one label before anything derives a domain
  // or groups rows, so the domain, the buckets and the tooltips all agree.
  const visitLabels = isVisitAxis(categoricalScale?.dataMapping)
    ? buildVisitLabelMap(data, categoricalScale.dataMapping)
    : null;

  data = data.map((d) => {
    if (visitLabels) {
      const canonical = visitLabels.get(
        visitKey(d[categoricalScale.dataMapping]),
      );
      if (canonical !== undefined) {
        d[categoricalScale.dataMapping] = canonical;
      }
    }
    for (const scale of scalesWithDataMapping) {
      if (scale?.dataMapping) {
        const normalized = normalizeFillValue(d[scale.dataMapping]);
        d[scale.dataMapping] =
          normalized == null || normalized === "" ? "-" : normalized;
      } else {
        d[scale.dataMapping] = undefined;
      }
    }
    return d;
  });

  const categoricalData =
    typeof data[0][categoricalScale.dataMapping] === "string"
      ? data.map((d) => d[categoricalScale.dataMapping])
      : data.map((d) => d[categoricalScale.dataMapping]).flat();

  const stackData = data.map((d) => d[stackScale?.dataMapping]);

  const dataLength =
    stackScale && stackData?.length > 0
      ? stackData.filter((d) => isValueValid(d)).length
      : categoricalData.filter((d) => isValueValid(d)).length;

  let categoricalDomain = categoricalScale.predefinedDomain
    ? categoricalScale.predefinedDomain
    : Array.isArray(data[0][categoricalScale.dataMapping])
      ? [
          ...new Set(data.flatMap((d) => "" + d[categoricalScale.dataMapping])),
        ].filter((d) => d)
      : [
          ...new Set(data.map((d) => "" + d[categoricalScale.dataMapping])),
        ].filter((d) => d);

  if (categoricalScale.numericScale) {
    const maxValue = Math.max(...categoricalDomain.map(Number));
    categoricalDomain = Array.from(
      { length: maxValue + 1 },
      (_, i) => i,
    ).reverse();
  }

  // A visit axis has one right order — the protocol's. Without this the domain
  // was whatever order rows arrived in (alphabetical for most datasets), which
  // read as "SCREENING, V1, W12, WEEK 4". This is not gated on the user's sort
  // setting: no chart wants its visits shuffled by default.
  if (visitLabels && !categoricalScale.predefinedDomain) {
    categoricalDomain = sortVisitDomain(
      categoricalDomain,
      data,
      categoricalScale.dataMapping,
    );
  }

  const { categorySortAxis, countSortAxis } = getBarplotSortAxes(
    categoricalScale.position,
    config.barplotSortSemanticsVersion,
  );

  if (config.xSortDirection !== "none" && config.xSortBy === categorySortAxis) {
    if (maxBarCount) {
      categoricalDomain = categoricalDomain.slice(0, maxBarCount);
    }

    categoricalDomain = sortDomain(
      categoricalDomain,
      config.xSortDirection,
      categoricalScale.position,
      data,
      categoricalScale.dataMapping,
      categoricalScale.dataType,
    );
  }

  if (config.onlyCategoriesWithData) {
    const countedCategoricalDomain = categoricalDomain.reduce((acc, d) => {
      if (data[0][categoricalScale.dataMapping] instanceof Array) {
        return {
          ...acc,
          [d]: data.filter(
            (ad) =>
              ad[categoricalScale.dataMapping].includes(d) &&
              (!stackScale || ad[stackScale?.dataMapping]),
          ).length,
        };
      }
      return {
        ...acc,
        [d]: data.filter(
          (ad) =>
            ad[categoricalScale.dataMapping] === d &&
            (!stackScale || ad[stackScale?.dataMapping]),
        ).length,
      };
    }, {});

    categoricalDomain = Object.keys(countedCategoricalDomain).filter(
      (d) => d && countedCategoricalDomain[d] > 0,
    );
  }

  if (config.xSortDirection !== "none" && config.xSortBy === countSortAxis) {
    const categoricalCounts = categoricalDomain.reduce((acc, category) => {
      if (data[0][categoricalScale.dataMapping] instanceof Array) {
        acc[category] = data.filter(
          (d) => "" + d[categoricalScale.dataMapping].includes(category),
        ).length;
      } else {
        acc[category] = data.filter(
          (d) => "" + d[categoricalScale.dataMapping] === category,
        ).length;
      }
      return acc;
    }, {});

    let sortedByCount = Object.keys(categoricalCounts)
      .sort((a, b) => categoricalCounts[a] - categoricalCounts[b])
      .filter((d) => d);

    if (maxBarCount) {
      sortedByCount = sortedByCount.slice(-maxBarCount);
    }

    const displayOrder = orderCategoriesForDisplay(
      sortedByCount,
      config.xSortDirection,
    );
    categoricalDomain = orderDomainForScale(
      displayOrder,
      categoricalScale.position,
    );
  } else if (maxBarCount) {
    categoricalDomain = categoricalDomain.slice(0, maxBarCount);
  }

  let subgroupDomain =
    subgroupScale && subgroupScale.predefinedDomain
      ? subgroupScale.predefinedDomain
      : [...new Set(data.map((d) => d[subgroupScale?.dataMapping]))].filter(
          (d) => d,
        );
  if (subgroupDomain.length === 0) {
    subgroupDomain = ["none"];
  }
  const stackDomain =
    stackScale && stackScale.predefinedDomain
      ? stackScale.predefinedDomain
      : [...new Set(data.map((d) => d[stackScale?.dataMapping]))].filter(
          (d) => d,
        );

  const totalDomain = [0, 0];

  if (Array.isArray(data[0][categoricalScale.dataMapping])) {
    data = data.reduce((acc, d) => {
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
      d[categoricalScale.dataMapping].forEach((a, i) => {
        acc.push({ ...d, [categoricalScale.dataMapping]: a });
      });
      return acc;
    }, []);
  }

  const nestedData = d3
    .groups(
      data,
      (d) => "" + d[categoricalScale.dataMapping],
      (d) => d[subgroupScale?.dataMapping],
    )
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    .filter(([key, subgroups]) => {
      return categoricalDomain.includes(key);
    })
    .map(([key, subgroups]) => {
      const obj = { [categoricalScale.dataMapping]: key };

      subgroups.forEach(([subkey, values]) => {
        subkey = subkey || "none";
        obj[subkey] = values.reduce((acc, val) => {
          const stackkey = val[stackScale?.dataMapping] || "none";

          const individualValue = config.barplotPercentage
            ? (1 / data.length) * 100
            : 1;
          acc[stackkey] = (acc[stackkey] || 0) + individualValue;

          return acc;
        }, {});
        const subkeyTotal = Object.values(obj[subkey]).reduce(
          (acc, val) => acc + val,
          0,
        );

        totalDomain[0] = Math.max(totalDomain[0], subkeyTotal);
      });
      return obj;
    });

  const stackedData = subgroupDomain.map((subgroupKey) => ({
    key: subgroupKey,
    stacks: d3
      .stack()
      .keys(stackDomain[0] ? stackDomain : ["none"])
      .value((d, key) => {
        key = key || "none";

        return d[subgroupKey] ? d[subgroupKey][key] || 0 : 0;
      })(nestedData),
  }));

  let scalesWithDomains = scales.map((scale) => {
    if (scale.type === "categorical") {
      if (config.onlyCategoriesWithData) {
        return {
          ...scale,
          domain: categoricalDomain.filter((d) => d),
        };
      }
      return { ...scale, domain: categoricalDomain };
    }
    if (scale.type === "subgroup" && scale.dataMapping !== "none") {
      return { ...subgroupScale, domain: subgroupDomain };
    }
    if (scale.type === "fill") {
      const predefinedRange = Object.values(scale?.predefinedRange || {});
      const paletteConfig = palettes[config?.palette || "Blue"] || {};

      return {
        ...scale,
        domain: stackDomain,
        range: [...predefinedRange, ...(paletteConfig.fill || [])],
        strokeRange: [...predefinedRange, ...(paletteConfig.stroke || [])],
      };
    }

    return scale;
  });

  scalesWithDomains = scalesWithDomains.filter((scale) => scale);

  const maxLabelLength =
    categoricalScale.position === "left" || config.verticalXAxisLabels
      ? Math.min(
          23,
          d3.max(categoricalDomain, (label) => label.length),
        ) || 23
      : 0;

  scalesWithDomains.push({
    type: "total",
    position: categoricalScale.position === "left" ? "bottom" : "left",
    domain: [totalDomain[0] * 1.1, totalDomain[1]],
    ...totalScale,
    label:
      totalScale?.label ||
      (config.barplotPercentage ? "Subject Percentage" : "Subject Count"),
    showPercentage: config.barplotPercentage,
  });

  return {
    scales: scalesWithDomains,
    nestedData,
    stackedData,
    maxLabelLength,
    maxCategoryLabelLength: longestCategoryLabel(categoricalDomain),
    plotClassName: config.plotClassName || categoricalScale.dataMapping,
    noData: dataLength === 0 || totalDomain[0] === 0,
    subjectData,
  };
};

const getNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = +value;
  return isNaN(num) ? undefined : num;
};

const getMaxDataValue = (data, scale) => {
  return (
    d3.max(data, (d) => getNumericValue(d[scale.dataMapping])) * 1.001 || 0
  );
};

const getMinDataValue = (data, scale) => {
  const min = d3.min(data, (d) => getNumericValue(d[scale.dataMapping]));
  return min != null ? min * 0.999 : 0;
};

const getContinuousDomain = (data, scale) => {
  return [getMaxDataValue(data, scale), getMinDataValue(data, scale)];
};

const isDateString = (value: unknown): boolean => {
  if (typeof value !== "string" || value === "") return false;
  if (!isNaN(+value)) return false; // plain number string, not a date
  return !isNaN(new Date(value).getTime());
};

const fieldHasDates = (data: any[], scale: any): boolean => {
  if (!scale?.dataMapping) return false;

  const sampleValue = data
    .map((d) => getScatterRawValue(d[scale.dataMapping]))
    .find((value) => isValueValid(value));

  return sampleValue ? isDateString(sampleValue) : false;
};

const orderDomainForScale = (displayOrder, position) =>
  position === "bottom" ? [...displayOrder].reverse() : displayOrder;

const MISSING_CATEGORY = "-";

const orderCategoriesForDisplay = (ascendingValues, sortDirection) => {
  const populatedValues = ascendingValues.filter(
    (value) => value !== MISSING_CATEGORY,
  );
  const missingValues = ascendingValues.filter(
    (value) => value === MISSING_CATEGORY,
  );
  const directedValues =
    sortDirection === "ascending"
      ? populatedValues
      : [...populatedValues].reverse();

  return [...directedValues, ...missingValues];
};

const compareCategoryValues = (a, b, dataType, valuesAreDates) => {
  if (dataType === "numeric" || (!valuesAreDates && !isNaN(+a) && !isNaN(+b))) {
    return Number(a) - Number(b);
  }
  if (valuesAreDates) {
    return new Date(a).getTime() - new Date(b).getTime();
  }
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const sortDomain = (
  domain,
  sortDirection,
  position,
  data,
  dataMapping,
  dataType,
) => {
  const populatedDomain = domain.filter((value) => value !== MISSING_CATEGORY);
  if (isVisitAxis(dataMapping) && data) {
    // Protocol order, from the visit_order the backend serializes. The previous
    // implementation sorted on `days` and coerced a missing value to 0, which
    // put every visit the study design has no day offset for — screening
    // included — in an arbitrary clump at the front.
    const ascendingTimepoints = sortVisitDomain(
      populatedDomain,
      data,
      dataMapping,
    );
    const displayOrder = orderCategoriesForDisplay(
      [
        ...ascendingTimepoints,
        ...domain.filter((value) => value === MISSING_CATEGORY),
      ],
      sortDirection,
    );
    return orderDomainForScale(displayOrder, position);
  }
  const valuesAreDates =
    dataType === "datetime" ||
    (populatedDomain.length > 0 && populatedDomain.every(isDateString));
  const ascendingValues = [...populatedDomain].sort((a, b) =>
    compareCategoryValues(a, b, dataType, valuesAreDates),
  );
  const displayOrder = orderCategoriesForDisplay(
    [
      ...ascendingValues,
      ...domain.filter((value) => value === MISSING_CATEGORY),
    ],
    sortDirection,
  );
  return orderDomainForScale(displayOrder, position);
};
